/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import * as RadixUiToggleGroup from "@radix-ui/react-toggle-group";
import type { ComponentProps } from "react";

import type { ThemeType } from "../../../../../global/emotion";
import { spacing } from "../../../../../global/emotion";
import type { ButtonProps } from "../../../_base/_core/Button/Button.emotion";
import { Button } from "../../../_base/_core/Button/Button.emotion";
import type { TabGroupProps } from "../TabGroup/TabGroup.emotion";

const disabledOpacityFullStyle: Interpolation = {
  opacity: "1 !important",
};
const itemButtonStyle: Interpolation = {
  WebkitTapHighlightColor: "transparent",
  marginRight: spacing.xs,
};
const itemButtonDisabledWhenSingleOnStyles: Record<
  TabGroupItemProps["variant"],
  Interpolation<ThemeType>
> = {
  primary: (theme) => ({
    backgroundColor: theme.semanticColors.primary,
    color: theme.semanticColors.secondary,
    cursor: "pointer",
    opacity: 1,
  }),
  select: (theme) => ({
    backgroundColor: theme.semanticColors.neutralsLowest,
    color: theme.semanticColors.primary,
    cursor: "pointer",
    opacity: 1,
  }),
};

export type TabGroupItemProps = ComponentProps<
  typeof RadixUiToggleGroup.Item
> & {
  disabledOpacityFull?: boolean;
  disabledWhenSingle?: boolean;
  onClick?: ButtonProps["onClick"];
  parentState: TabGroupProps["value"];
  variant?: "primary" | "select";
};

export const TabGroupItem = ({
  children,
  disabledOpacityFull = true,
  disabledWhenSingle = true,
  onClick,
  parentState,
  value,
  variant = "primary",
  ...props
}: TabGroupItemProps) => {
  const toggleGroupItemOn =
    parentState === value || parentState?.includes(value);

  return (
    <RadixUiToggleGroup.Item
      key={`tab-group-item-${children}`}
      aria-label={value}
      asChild
      value={value}
      disabled={disabledWhenSingle && parentState === value}
      {...props}
    >
      <Button
        onClick={onClick}
        size="s"
        css={[
          itemButtonStyle,
          disabledWhenSingle && toggleGroupItemOn
            ? itemButtonDisabledWhenSingleOnStyles[variant]
            : null,
          disabledOpacityFull && toggleGroupItemOn
            ? disabledOpacityFullStyle
            : null,
        ]}
        variant={
          variant === "select"
            ? "transparentPrimary"
            : toggleGroupItemOn
              ? "primary"
              : "secondary"
        }
      >
        {children}
      </Button>
    </RadixUiToggleGroup.Item>
  );
};
